(() => {
  'use strict';

  if (window.__lldgMotionLoaded) {
    document.dispatchEvent(new CustomEvent('lldg:motion:refresh'));
    return;
  }
  window.__lldgMotionLoaded = true;

  const root = document.documentElement;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const motionEnabled = root.dataset.motionEnabled !== 'false';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopEditorialQuery = window.matchMedia(
    '(min-width: 1041px) and (prefers-reduced-motion: no-preference)',
  );

  let revealObserver = null;
  let headerResizeObserver = null;
  let revealItems = [];
  let headers = [];
  let heroPairs = [];
  let materialWords = [];
  let projectImages = [];
  let frame = 0;
  let refreshFrame = 0;

  const clamp = (value) => Math.min(Math.max(value, 0), 1);
  const listenToQuery = (query, callback, add) => {
    const method = add ? 'addEventListener' : 'removeEventListener';
    if (typeof query[method] === 'function') query[method]('change', callback);
    else query[add ? 'addListener' : 'removeListener'](callback);
  };

  const resetEditorialMotion = () => {
    heroPairs.forEach(({ media }) => {
      media.style.removeProperty('--hero-frame-scale');
      media.style.removeProperty('--hero-inset-top');
      media.style.removeProperty('--hero-inset-side');
      media.style.removeProperty('--hero-inset-bottom');
    });
    materialWords.forEach((element) => {
      element.style.removeProperty('--material-position');
      element.style.removeProperty('--material-size');
    });
    projectImages.forEach((image) => image.style.removeProperty('--image-drift'));
  };

  const revealEverything = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-reveal]')) {
      scope.classList.add('is-visible');
    }
    if (scope && typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll('[data-reveal]').forEach((item) => {
        item.classList.add('is-visible');
      });
    }
  };

  const setupReveals = () => {
    if (revealObserver) revealObserver.disconnect();
    revealObserver = null;
    revealItems = Array.from(document.querySelectorAll('[data-reveal]'));

    if (
      designMode ||
      !motionEnabled ||
      motionQuery.matches ||
      !('IntersectionObserver' in window)
    ) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      root.classList.add('motion-ready');
      return;
    }

    revealItems.forEach((item) => {
      if (item.getBoundingClientRect().top < window.innerHeight * 0.92) {
        item.classList.add('is-visible');
      }
    });
    root.classList.add('motion-ready');

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' },
    );

    revealItems
      .filter((item) => !item.classList.contains('is-visible'))
      .forEach((item) => revealObserver.observe(item));
  };

  const collectMotionTargets = () => {
    headers = Array.from(document.querySelectorAll('[data-site-header]'));
    if ('ResizeObserver' in window) {
      if (!headerResizeObserver) headerResizeObserver = new ResizeObserver(scheduleUpdate);
      headerResizeObserver.disconnect();
      headers.forEach((header) => headerResizeObserver.observe(header));
    }
    heroPairs = Array.from(document.querySelectorAll('.hero'))
      .map((hero) => ({
        hero,
        media: hero.querySelector('.hero-media'),
        enabled: hero.dataset.scrollFraming !== 'false',
      }))
      .filter(({ media }) => Boolean(media));
    materialWords = Array.from(document.querySelectorAll('.material-words'));
    projectImages = Array.from(document.querySelectorAll('.project-image'));
  };

  const updateEditorialMotion = () => {
    if (!motionEnabled || motionQuery.matches) {
      resetEditorialMotion();
      return;
    }

    const viewportHeight = window.innerHeight;
    const desktopMotionEnabled = desktopEditorialQuery.matches && !designMode;
    heroPairs.forEach(({ hero, media, enabled }) => {
      if (!desktopMotionEnabled || !enabled) {
        media.style.removeProperty('--hero-frame-scale');
        media.style.removeProperty('--hero-inset-top');
        media.style.removeProperty('--hero-inset-side');
        media.style.removeProperty('--hero-inset-bottom');
        return;
      }
      const rect = hero.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(rect.height * 0.9, 1));
      media.style.setProperty('--hero-frame-scale', (1 - progress * 0.075).toFixed(4));
      media.style.setProperty('--hero-inset-top', `${(progress * 1.8).toFixed(3)}%`);
      media.style.setProperty('--hero-inset-side', `${(progress * 1.5).toFixed(3)}%`);
      media.style.setProperty('--hero-inset-bottom', `${(progress * 3).toFixed(3)}%`);
    });

    materialWords.forEach((element) => {
      const section = element.closest('.approach-manifesto');
      if (!section) return;
      if (section.dataset.maskMotion === 'false') {
        element.style.removeProperty('--material-position');
        element.style.removeProperty('--material-size');
        return;
      }
      const rect = section.getBoundingClientRect();
      const progress = clamp(
        (viewportHeight - rect.top) / Math.max(viewportHeight + rect.height, 1),
      );
      const mobile = window.innerWidth <= 767;
      const startPosition = mobile ? 34 : 30;
      const positionTravel = mobile ? 32 : 42;
      const startSize = mobile ? 138 : 145;
      const sizeTravel = mobile ? 20 : 30;
      element.style.setProperty(
        '--material-position',
        `${(startPosition + progress * positionTravel).toFixed(2)}%`,
      );
      element.style.setProperty(
        '--material-size',
        `${(startSize - progress * sizeTravel).toFixed(2)}%`,
      );
    });

    projectImages.forEach((image) => {
      if (!desktopMotionEnabled) {
        image.style.removeProperty('--image-drift');
        return;
      }
      const projectSection = image.closest('[data-project-stack]');
      if (projectSection && projectSection.dataset.imageDrift === 'false') {
        image.style.removeProperty('--image-drift');
        return;
      }
      const frameElement = image.parentElement;
      if (!frameElement) return;
      const rect = frameElement.getBoundingClientRect();
      const progress = clamp(
        (viewportHeight - rect.top) / Math.max(viewportHeight + rect.height, 1),
      );
      image.style.setProperty('--image-drift', `${((progress - 0.5) * 32).toFixed(2)}px`);
    });
  };

  const updateScrollState = () => {
    frame = 0;
    const scrollable = root.scrollHeight - window.innerHeight;
    const progress = scrollable > 0 ? clamp(window.scrollY / scrollable) : 0;
    root.style.setProperty('--scroll-progress', String(progress));
    headers.forEach((header) => {
      const stickyEnabled = header.dataset.stickyHeader !== 'false';
      const mayBeTransparent =
        header.dataset.transparentHome !== 'false' && heroPairs.length > 0;
      const solidHeader = !mayBeTransparent || window.scrollY > 72;
      header.classList.toggle(
        'is-scrolled',
        stickyEnabled && solidHeader,
      );
      header.classList.toggle('is-solid', !stickyEnabled && solidHeader);
    });
    const headerHeight = Math.ceil(Math.max(0, ...headers.map((header) => header.getBoundingClientRect().height)));
    const headerHeightValue = `${headerHeight}px`;
    if (root.style.getPropertyValue('--lldg-header-height') !== headerHeightValue) {
      root.style.setProperty('--lldg-header-height', headerHeightValue);
    }
    updateEditorialMotion();
  };

  const scheduleUpdate = () => {
    if (!frame) frame = window.requestAnimationFrame(updateScrollState);
  };

  const refresh = () => {
    refreshFrame = 0;
    collectMotionTargets();
    setupReveals();
    if (designMode) revealEverything(document);
    scheduleUpdate();
  };

  const scheduleRefresh = () => {
    if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
    refreshFrame = window.requestAnimationFrame(refresh);
  };

  const handleMotionPreference = () => {
    if (motionQuery.matches || designMode) revealEverything(document);
    refresh();
  };

  const initialize = () => {
    if (designMode) root.classList.add('shopify-design-mode');
    refresh();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    listenToQuery(motionQuery, handleMotionPreference, true);
    listenToQuery(desktopEditorialQuery, scheduleUpdate, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('lldg:motion:refresh', scheduleRefresh);
  document.addEventListener('shopify:section:load', (event) => {
    if (designMode) revealEverything(event.target);
    scheduleRefresh();
  });
  document.addEventListener('shopify:section:unload', scheduleRefresh);
  document.addEventListener('shopify:section:reorder', scheduleRefresh);
  document.addEventListener('shopify:section:select', (event) => {
    revealEverything(event.target);
    scheduleUpdate();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const reveal = event.target.closest('[data-reveal]');
    if (reveal) reveal.classList.add('is-visible');
    revealEverything(event.target);
    scheduleUpdate();
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-mobile-menu] a');
    if (link) {
      const menu = link.closest('[data-mobile-menu]');
      if (menu) menu.removeAttribute('open');
      return;
    }

    document.querySelectorAll('[data-mobile-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menus = Array.from(document.querySelectorAll('[data-mobile-menu][open]'));
    menus.forEach((menu) => menu.removeAttribute('open'));
    const summary = menus[0] && menus[0].querySelector('summary');
    if (summary) summary.focus();
  });
})();
